import { motion } from 'framer-motion'
import Link from '../components/LocalizedLink'
import { Calculator, Settings, TrendingUp, FileSpreadsheet, Download, ArrowRight } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import SEO from '../components/SEO'

const BASE = import.meta.env.BASE_URL

const translations = {
  el: {
    hero: {
      title: 'Κοστολόγηση Παραγωγής για Εργαστήρια Λαμαρίνας',
      subtitle: 'Υπολογίστε το πραγματικό κόστος παραγωγής κάθε εξαρτήματος — υλικό, μηχανήματα, εργατικά και ρεύμα — πριν στείλετε την προσφορά, όχι αφού είναι πολύ αργά.',
      cta1: 'Δωρεάν Δοκιμή',
      cta2: 'Δείτε Τιμές',
    },
    intro: {
      title: 'Τι είναι η κοστολόγηση παραγωγής',
      body: 'Η κοστολόγηση παραγωγής είναι η διαδικασία υπολογισμού του πραγματικού κόστους κατασκευής ενός εξαρτήματος πριν αυτό κατασκευαστεί — υλικό, κοπή, κάμψη, εργατικά και μηχανήματα συνδυάζονται σε ένα τελικό κόστος ανά τεμάχιο. Το Workshop Assistant αυτοματοποιεί ολόκληρο αυτόν τον υπολογισμό ώστε να μη βασίζεστε σε εκτιμήσεις με το μάτι.',
    },
    features: {
      title: 'Πώς υπολογίζει το κόστος το Workshop Assistant',
      items: [
        { icon: 'calc', title: 'Αυτόματος Υπολογισμός ανά Εξάρτημα', description: 'Σαρώνει τα εισαγόμενα αρχεία και υπολογίζει κόστος υλικού, κοπής και κάμψης σε πραγματικό χρόνο για κάθε εξάρτημα ξεχωριστά.' },
        { icon: 'settings', title: 'Πλήρως Παραμετροποιήσιμο', description: 'Ρυθμίστε τιμές μηχανημάτων (laser, ψαλίδι, κάμψη, router), κόστος υλικών, ταρίφες ρεύματος και εργατικών ώστε να ταιριάζουν ακριβώς με το εργαστήριό σας.' },
        { icon: 'trend', title: 'Ρύθμιση Περιθωρίου Κέρδους', description: 'Ορίστε markup ανά προσφορά ή ανά πελάτη και δείτε άμεσα πώς επηρεάζει την τελική τιμή.' },
        { icon: 'excel', title: 'Αναλυτικές Αναφορές Excel', description: 'Κάθε προσφορά συνοδεύεται από επαγγελματική αναφορά Excel με πλήρη ανάλυση κόστους παραγωγής ανά εξάρτημα.' },
      ],
    },
    faq: [
      { q: 'Τι περιλαμβάνει η κοστολόγηση παραγωγής στο Workshop Assistant;', a: 'Κόστος υλικού (βάσει πυκνότητας και τιμής ανά κιλό), κόστος κοπής (laser/ψαλίδι/router), κόστος κάμψης, εργατικά και ρεύμα, συν το περιθώριο κέρδους που ορίζετε.' },
      { q: 'Μπορώ να αλλάξω τις τιμές μηχανημάτων και υλικών;', a: 'Ναι, ο Επεξεργαστής Παραμέτρων σάς επιτρέπει να προσαρμόσετε όλες τις τιμές μηχανημάτων, υλικών, ρεύματος και εργατικών οποιαδήποτε στιγμή.' },
      { q: 'Λειτουργεί για CNC router εκτός από έλασμα;', a: 'Ναι, το Workshop Assistant υπολογίζει κόστος παραγωγής τόσο για εξαρτήματα ελάσματος όσο και για CNC router, με ξεχωριστές ρυθμίσεις για κάθε μέθοδο.' },
    ],
    cta: { title: 'Δείτε την Κοστολόγηση Παραγωγής σε Δράση', subtitle: 'Κατεβάστε τη δωρεάν δοκιμή και υπολογίστε το πραγματικό κόστος παραγωγής των εξαρτημάτων σας σε λεπτά.', btn: 'Δωρεάν Δοκιμή' },
  },
  en: {
    hero: {
      title: 'Production Costing for Sheet Metal Fabrication Shops',
      subtitle: 'Calculate the real production cost of every part — material, machine time, labor, and electricity — before you send the quote, not after it\'s too late.',
      cta1: 'Download Free Trial',
      cta2: 'View Pricing',
    },
    intro: {
      title: 'What is production costing',
      body: 'Production costing is the process of calculating the true cost of manufacturing a part before it\'s made — material, cutting, bending, labor, and machine time combined into one final per-part cost. Workshop Assistant automates that whole calculation so you\'re not relying on rough eyeball estimates.',
    },
    features: {
      title: 'How Workshop Assistant calculates production cost',
      items: [
        { icon: 'calc', title: 'Automatic Per-Part Calculation', description: 'Scans imported files and calculates material, cutting, and bending cost in real time for every individual part.' },
        { icon: 'settings', title: 'Fully Configurable', description: 'Set machine rates (laser, guillotine, bending, router), material costs, electricity tariffs, and labor rates to match your exact workshop.' },
        { icon: 'trend', title: 'Profit Margin Control', description: 'Set markup per offer or per customer and see instantly how it changes the final price.' },
        { icon: 'excel', title: 'Detailed Excel Reports', description: 'Every offer comes with a professional Excel report showing the full production cost breakdown per part.' },
      ],
    },
    faq: [
      { q: 'What does production costing include in Workshop Assistant?', a: 'Material cost (based on density and price per kg), cutting cost (laser/guillotine/router), bending cost, labor, and electricity, plus whatever profit margin you set.' },
      { q: 'Can I change machine and material prices?', a: 'Yes — the Parameters Editor lets you adjust every machine, material, electricity, and labor rate at any time.' },
      { q: 'Does it work for CNC router parts too, not just sheet metal?', a: 'Yes, Workshop Assistant calculates production cost for both sheet metal and CNC router parts, with separate settings for each method.' },
    ],
    cta: { title: 'See Production Costing in Action', subtitle: 'Download the free trial and calculate the real production cost of your parts in minutes.', btn: 'Download Free Trial' },
  },
}

const icons = { calc: <Calculator className="h-10 w-10" />, settings: <Settings className="h-10 w-10" />, trend: <TrendingUp className="h-10 w-10" />, excel: <FileSpreadsheet className="h-10 w-10" /> }

const ProductionCosting = () => {
  const { lang } = useLanguage()
  const t = translations[lang]

  return (
    <div className="overflow-hidden">
      <SEO
        title="Production Costing Software for Metal Fabrication"
        description="Calculate real production costs for sheet metal and CNC router parts — material, machine time, labor, and margin — automatically with Workshop Assistant."
        path="/production-costing"
        faq={t.faq}
      />

      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="section-container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-5xl md:text-6xl font-bold mb-6">{t.hero.title}</motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-xl opacity-90 mb-8">{t.hero.subtitle}</motion.p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/download" className="flex items-center justify-center space-x-2 px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-200 shadow-lg">
                <Download className="h-5 w-5" /><span>{t.hero.cta1}</span>
              </Link>
              <Link to="/pricing" className="flex items-center justify-center space-x-2 px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-all duration-200">
                <span>{t.hero.cta2}</span><ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section-container bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold mb-4">{t.intro.title}</h2>
            <p className="text-lg text-gray-600">{t.intro.body}</p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
            <img src={`${BASE}screenshots/calculator.png?v=2`} alt={t.hero.title} className="w-full h-auto object-cover" />
          </div>
        </div>
      </section>

      <section className="section-container bg-gray-50">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">{t.features.title}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {t.features.items.map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="card">
              <div className="text-primary-600 mb-4">{icons[f.icon]}</div>
              <h3 className="text-xl font-semibold mb-2">{f.title}</h3>
              <p className="text-gray-600">{f.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="section-container bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center">FAQ</h2>
          <div className="space-y-4">
            {t.faq.map((item, i) => (
              <details key={i} className="card group">
                <summary className="font-semibold cursor-pointer list-none flex justify-between items-center">
                  {item.q}
                  <span className="text-primary-600 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-gray-600 mt-3">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="section-container bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">{t.cta.title}</h2>
          <p className="text-xl mb-8 opacity-90">{t.cta.subtitle}</p>
          <Link to="/download" className="inline-flex items-center space-x-2 px-8 py-4 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-all duration-200 shadow-lg">
            <Download className="h-5 w-5" /><span>{t.cta.btn}</span>
          </Link>
        </div>
      </section>
    </div>
  )
}

export default ProductionCosting
